// v2 tower — a square stone watchtower: a battered two-step plinth carrying a
// tall square shaft with pale quoins and a string course, topped by a corbelled
// timber hoarding (the wooden fighting gallery, jettied out on eight corbel
// blocks) under a slate pyramid roof flying a red pennant. A brazier and a stack
// of cut stone wait in the yard. Footprint-native 2x2. Ported 1:1 from
// age-of-mcp's v2/tower.ts, using the library's shared `foundationSlab` and
// `village` (buildScaffold) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, village, climb, type Part } from "../../lib";

const BASE  = 0.14;
const STONE = "#878d92";
const QUOIN = "#9aa1a8";
const TRIM  = "#6b7074";
const WOODY = "#7a5a34";     // hoarding timber
const DARK  = "#1a1a1a";
const PLINTH_TOP = 0.74;     // top of the two-step plinth
const SW = 0.78;             // shaft width

// The battered two-step plinth at the base.
function plinth(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(1.14, 0.34, 1.14), color: "#737a80",
      position: [0, BASE + 0.17, 0] },
    { geometry: new THREE.BoxGeometry(0.96, 0.26, 0.96), color: "#7c838a",
      position: [0, 0.61, 0] },
  ];
}

// The square shaft, `h` tall on the plinth.
function shaft(h: number): Part {
  return { geometry: new THREE.BoxGeometry(SW, h, SW), color: STONE,
    position: [0, PLINTH_TOP + h / 2, 0] };
}

// Pale quoin strips up the shaft corners.
function quoins(h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-SW / 2, -SW / 2], [SW / 2, -SW / 2], [-SW / 2, SW / 2], [SW / 2, SW / 2]] as const) {
    out.push({ geometry: new THREE.BoxGeometry(0.1, h, 0.1), color: QUOIN,
      position: [x, PLINTH_TOP + h / 2, z] });
  }
  return out;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.84)];
  if (s === 1) {
    return [foundationSlab(1.84), ...plinth(), shaft(0.7),
      ...village.buildScaffold(1.0, 1.0, 1.5)];
  }
  if (s === 2) {
    return [foundationSlab(1.84), ...plinth(), shaft(1.66), ...quoins(1.66),
      { geometry: new THREE.BoxGeometry(0.86, 0.06, 0.86), color: TRIM,
        position: [0, 1.5, 0] },
      ...village.buildScaffold(1.0, 1.0, 2.5)];
  }

  const shaftH = 1.66;
  const top = PLINTH_TOP + shaftH;       // 2.40
  const parts: Part[] = [
    foundationSlab(1.84),
    ...plinth(),
    shaft(shaftH),
    ...quoins(shaftH),
    // String course band partway up.
    { geometry: new THREE.BoxGeometry(0.86, 0.06, 0.86), color: TRIM,
      position: [0, 1.5, 0] },
  ];
  // Eight corbel blocks jettying the hoarding out past the shaft.
  for (const [x, z] of [
    [-0.44, -0.44], [0, -0.44], [0.44, -0.44], [-0.44, 0],
    [0.44, 0], [-0.44, 0.44], [0, 0.44], [0.44, 0.44],
  ] as const) {
    parts.push({ geometry: new THREE.BoxGeometry(0.12, 0.18, 0.12), color: TRIM,
      position: [x, top - 0.07, z] });
  }
  parts.push(
    // The corbelled timber hoarding with watch slits on three faces.
    { geometry: new THREE.BoxGeometry(1.06, 0.5, 1.06), color: WOODY,
      position: [0, top + 0.27, 0] },
    { geometry: new THREE.BoxGeometry(0.07, 0.20, 0.05), color: DARK,
      position: [0, top + 0.26, 0.545] },
    { geometry: new THREE.BoxGeometry(0.05, 0.20, 0.07), color: DARK,
      position: [0.545, top + 0.26, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.20, 0.07), color: DARK,
      position: [-0.545, top + 0.26, 0] },
    // Slate pyramid roof (4-sided cone, flats facing the compass).
    { geometry: new THREE.ConeGeometry(0.85, 0.5, 4), color: "#4a4f55",
      position: [0, top + 0.77, 0], rotation: [0, Math.PI / 4, 0] },
    // Banner pole and pennant on the apex.
    { geometry: new THREE.CylinderGeometry(0.025, 0.025, 0.36, 4), color: "#3a2412",
      position: [0, 3.5, 0] },
    { geometry: new THREE.BoxGeometry(0.30, 0.16, 0.03), color: "#a83a3a",
      position: [0.15, 3.58, 0] },
    // Door through the plinth on the road side, arrow slits up the shaft.
    { geometry: new THREE.BoxGeometry(0.26, 0.50, 0.07), color: "#4a2c18",
      position: [0, 0.39, 0.575] },
    { geometry: new THREE.BoxGeometry(0.07, 0.24, 0.06), color: DARK,
      position: [0, 1.3, 0.40] },
    { geometry: new THREE.BoxGeometry(0.07, 0.24, 0.06), color: DARK,
      position: [0, 1.9, 0.40] },
    // Yard: a brazier and a stack of cut stone.
    { geometry: new THREE.CylinderGeometry(0.04, 0.06, 0.30, 6), color: DARK,
      position: [0.62, 0.29, 0.58] },
    { geometry: new THREE.CylinderGeometry(0.11, 0.07, 0.10, 8), color: "#3a3a3a",
      position: [0.62, 0.47, 0.58] },
    { geometry: new THREE.ConeGeometry(0.08, 0.20, 6), color: "#f08a2c",
      position: [0.62, 0.60, 0.58] },
    { geometry: new THREE.BoxGeometry(0.22, 0.14, 0.22), color: "#7e7a74",
      position: [-0.62, 0.21, 0.55] },
    { geometry: new THREE.BoxGeometry(0.16, 0.12, 0.16), color: "#8d8d8d",
      position: [-0.60, 0.34, 0.53] },
  );
  return parts;
}

export const towerV2 = defineModel({
  id: "tower-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Tower (v2)",
  tags: ["building","tower","low-poly","age-of-mcp","fable","military"],
  summary: "Low-poly tower.",
  description: "A tall stone watchtower with a crenellated top, trimmed with gold and gems — a grand prestige landmark.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
  stations: [
    { id: "face", position: [0, 0.14, 0.62], facing: Math.PI, description: "The tower face a sentry climbs." },
  ],
  interactions: [
    { id: "climb", label: "Climbing the tower", station: "face", variants: ["complete"], ...climb() },
  ],
});
